import { useState } from "react";
import SvgCalendar from "../../svg/SvgCalendar";
import { MdCardMembership } from "react-icons/md";
import { FaUsers } from "react-icons/fa";

export default function CalendarProfesional({ filteredData, fechaMembre }) {
  const [activeIndex, setActiveIndex] = useState(0);

  // Definir colores para cada horario
  const scheduleColors = {
    mañana: "bg-yellow-300",
    tarde: "bg-blue-300",
    noche: "bg-purple-300",
    membre: "bg-[#ecb0f18f]", // Color para los días de membresía
  };

  // Mapear los nombres de los días a índices para compararlos con los días del calendario
  const daysMap = {
    D: 0, // Domingo
    L: 1, // Lunes
    M: 2, // Martes
    MI: 3, // Miércoles
    J: 4, // Jueves
    V: 5, // Viernes
    S: 6, // Sábado
  };

  // Mapa para convertir nombres completos de días a abreviaturas
  const fullDayToAbbr = {
    Domingo: "D",
    Lunes: "L",
    Martes: "M",
    Miércoles: "MI",
    Jueves: "J",
    Viernes: "V",
    Sábado: "S",
  };

  // Procesar los días de servicios comunes
  const commonDays = filteredData.flatMap((item) => {
    const services = item?.mother?.services || [];
    return services.map((service) => ({
      days: service.days || [], // Ej: ['L', 'M', 'J']
      schedule: service.schedule || "", // Ej: "mañana"
    }));
  });

  // Crear un mapa de días y sus colores para servicios comunes
  const commonDayColorMap = {};
  commonDays.forEach(({ days, schedule }) => {
    days.forEach((day) => {
      const dayName = Object.keys(daysMap).find((key) => key.startsWith(day));
      if (dayName) {
        if (!commonDayColorMap[dayName]) {
          commonDayColorMap[dayName] = [];
        }
        if (!commonDayColorMap[dayName].includes(schedule)) {
          commonDayColorMap[dayName].push(schedule);
        }
      }
    });
  });

  // Procesar los días de servicios de membresía
  const membreDays = (fechaMembre || []).map((day) => ({
    day: fullDayToAbbr[day] || day, // Convertir a abreviatura si es posible
    schedule: "membre", // Todos los días de membresía tienen el mismo color
  }));

  // Crear un mapa de días y sus colores para servicios de membresía
  const membreDayColorMap = {};
  membreDays.forEach(({ day }) => {
    const dayName = Object.keys(daysMap).find((key) => key.startsWith(day));
    if (dayName) {
      if (!membreDayColorMap[dayName]) {
        membreDayColorMap[dayName] = [];
      }
      if (!membreDayColorMap[dayName].includes("membre")) {
        membreDayColorMap[dayName].push("membre");
      }
    }
  });

  // Obtener el número total de días en el mes actual
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  const totalDaysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();

  // Obtener el día de la semana en el que comienza el mes (0 = Domingo, 1 = Lunes, etc.)
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();



  return (
    <div className="overflow-hidden">
      {/* Controles */}
      <div className="flex justify-center mb-4">
        {[0, 1].map((index) => (
          <button
            key={index}
            onClick={() => setActiveIndex(index)}
            className={`mx-2 px-4 py-2 rounded-full ${
              activeIndex === index ? "bg-blue-200 text-white" : "bg-gray-200"
            }`}
          >
            {index === 0 ? <FaUsers className="text-[#aab125]" /> : <MdCardMembership  className="text-[#dd4eea]" />}
          </button>
        ))}
      </div>

      {/* Slider */}
      <div className="relative ms:h-[500px] md:h-full ">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${activeIndex * 100}%)` }}
        >
          {/* Primer calendario: Servicios comunes */}
          <div className="w-full flex-shrink-0 px-4">
            <div className="mt-8">

            <div className="flex flex-row justify-around">
              <h3 className="text-xl font-bold text-gray-700 ml-4 mb-4">
                Calendario
              </h3>
              <SvgCalendar />
              </div>

              <div className="grid grid-cols-7 gap-2 text-center">
                {/* Header del calendario */}
                {["D", "L", "M", "MI", "J", "V", "S"].map((day) => (
                  <div key={day} className="font-medium text-gray-600">
                    {day}
                  </div>
                ))}
                {/* Espacios en blanco para los días anteriores al primer día del mes */}
                {[...Array(firstDayOfMonth).keys()].map((blankDay) => (
                  <div key={`blank-${blankDay}`} className="p-2 aspect-square"></div>
                ))}
                {/* Días del mes */}
                {[...Array(totalDaysInMonth).keys()].map((day) => {
                  const date = new Date(currentYear, currentMonth, day + 1);
                  const dayOfWeek = date.getDay(); // Índice del día de la semana
                  const dayName = Object.keys(daysMap).find(
                    (_, idx) => idx === dayOfWeek
                  );
                  const daySchedules = commonDayColorMap[dayName] || [];

                  return (
                    <div
                      key={day + 1}
                      className="relative p-2 rounded-lg border aspect-square flex items-center justify-center"
                    >
                      {/* Colores de los horarios */}
                      {daySchedules.map((schedule, index) => (
                        <div
                          key={index}
                          className={`absolute top-0 left-0 w-full ${scheduleColors[schedule]}`}
                          style={{
                            height: `${100 / daySchedules.length}%`,
                            top: `${(100 / daySchedules.length) * index}%`,
                          }}
                        ></div>
                      ))}
                      {/* Día del mes */}
                      <span className="relative z-10 text-gray-800">
                        {day + 1}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Segundo calendario: Servicios de membresía */}
          <div className="w-full flex-shrink-0 px-4">
            <div className="mt-8">

              <div className="flex flex-row justify-around">

              <h3 className="text-xl font-bold text-gray-700 ml-4 mb-4">
                 Membresía
              </h3>
              <SvgCalendar />
              </div>

              <div className="grid grid-cols-7 gap-2 text-center">
                {/* Header del calendario */}
                {["D", "L", "M", "MI", "J", "V", "S"].map((day) => (
                  <div key={day} className="font-medium text-gray-600">
                    {day}
                  </div>
                ))}
                {/* Espacios en blanco para los días anteriores al primer día del mes */}
                {[...Array(firstDayOfMonth).keys()].map((blankDay) => (
                  <div key={`blank-${blankDay}`} className="p-2 aspect-square"></div>
                ))}
                {/* Días del mes */}
                {[...Array(totalDaysInMonth).keys()].map((day) => {
                  const date = new Date(currentYear, currentMonth, day + 1);
                  const dayOfWeek = date.getDay(); // Índice del día de la semana
                  const dayName = Object.keys(daysMap).find(
                    (_, idx) => idx === dayOfWeek
                  );
                  const daySchedules = membreDayColorMap[dayName] || [];

                  return (
                    <div
                      key={day + 1}
                      className="relative p-2 rounded-lg border aspect-square flex items-center justify-center"
                    >
                      {/* Colores de los horarios */}
                      {daySchedules.map((schedule, index) => (
                        <div
                          key={index}
                          className={`absolute top-0 left-0 w-full ${scheduleColors[schedule]}`}
                          style={{
                            height: `${100 / daySchedules.length}%`,
                            top: `${(100 / daySchedules.length) * index}%`,
                          }}
                        ></div>
                      ))}
                      {/* Día del mes */}
                      <span className="relative z-10 text-gray-800">
                        {day + 1}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


